import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiBookOpen, FiPlus, FiEdit, FiTrash2, FiFilter, FiUsers, FiUserPlus, FiUsers as FiBulkUsers, FiUserMinus, FiCalendar, FiFileText } from 'react-icons/fi';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import AddElectiveModal from './AddElectiveModal';
import EditElectiveModal from './EditElectiveModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AssignElectiveStudentModal from './AssignElectiveStudentModal';
import BulkAssignElectiveModal from './BulkAssignElectiveModal';
import RemoveElectiveStudentModal from './RemoveElectiveStudentModal';
import CreateAttendanceModal from './CreateAttendanceModal';
import Notification from '../common/Notification';
import Tooltip from '../common/Tooltip';

const ElectiveManagementContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const AddButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  touch-action: manipulation;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  width: 300px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const FilterButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--background-tertiary);
    border-color: var(--primary-color);
  }
  
  &.active {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const ElectivesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ElectiveCard = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ElectiveHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ElectiveInfo = styled.div`
  flex: 1;
`;

const ElectiveCode = styled.h3`
  color: var(--primary-color);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const ElectiveName = styled.h4`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PrimaryActionButton = styled.button`
  padding: 0.5rem !important;
  background-color: var(--primary-color) !important;
  background: var(--primary-color) !important;
  border: none !important;
  border-color: transparent !important;
  color: var(--text-inverse) !important;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: none !important;
  outline: none !important;
  
  &:hover {
    background-color: var(--primary-dark) !important;
    background: var(--primary-dark) !important;
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
`;

const ElectiveDescription = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ElectiveInstructor = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.875rem;
  margin-bottom: 1rem;
  
  svg {
    color: var(--primary-color);
  }
`;

const ElectiveStats = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.75rem;
  
  svg {
    color: var(--primary-color);
  }
`;

const ElectiveDates = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &.active {
    background-color: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
  }
  
  &.inactive {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
  
  &.upcoming {
    background-color: rgba(246, 224, 94, 0.1);
    color: var(--warning-dark);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 1rem;
    margin: 0;
  }
`;

function ElectiveManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [electives, setElectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showCreateAttendanceModal, setShowCreateAttendanceModal] = useState(false);
  const [selectedElective, setSelectedElective] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });

  useEffect(() => {
    fetchElectives();
  }, []);

  const fetchElectives = async () => {
    try {
      setLoading(true);
      const response = await apiService.getElectives();
      setElectives(response.electives || []);
    } catch (error) {
      console.error('Error fetching electives:', error);
      setElectives([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredElectives = electives.filter(elective => {
    // Get all instructor names
    const instructorNames = elective.instructors && Array.isArray(elective.instructors) && elective.instructors.length > 0
      ? elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(' ')
      : `${elective.instructor_first_name || ''} ${elective.instructor_last_name || ''}`.trim();
    
    const matchesSearch = 
      elective.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      elective.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructorNames.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle MySQL boolean values (0/1) correctly
    const isActive = elective.is_active === 1 || elective.is_active === true;
    const matchesFilter = activeFilter === 'all' || 
      (activeFilter === 'active' && isActive) ||
      (activeFilter === 'inactive' && !isActive);
    
    return matchesSearch && matchesFilter;
  });

  const getElectiveStatus = (elective) => {
    // Handle MySQL boolean values (0/1) correctly
    // Status badge directly reflects the is_active flag for intuitive admin control
    const isActive = elective.is_active === 1 || elective.is_active === true;
    
    if (!isActive) return 'inactive';
    
    // If is_active is true, check dates to show 'upcoming' vs 'active'
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for date comparison
    
    const startDate = new Date(elective.start_date);
    startDate.setHours(0, 0, 0, 0);
    
    // If start date is in the future, show as 'upcoming'
    if (today < startDate) return 'upcoming';
    
    // Otherwise show as 'active' (covers current date range and past dates when manually set to active)
    return 'active';
  };

  const handleAddElective = () => {
    setShowAddModal(true);
  };

  const handleElectiveAdded = (newElective) => {
    setElectives(prev => [newElective, ...prev]);
  };

  const handleEditElective = (elective) => {
    setSelectedElective(elective);
    setShowEditModal(true);
  };

  const handleElectiveUpdated = (updatedElective) => {
    setElectives(prev => prev.map(elective => 
      elective.id === updatedElective.id ? updatedElective : elective
    ));
    // Refresh to ensure status is updated correctly
    fetchElectives();
  };

  const handleStudentAssigned = (student) => {
    // Refresh electives to update enrollment count
    fetchElectives();
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Student Assigned',
      message: `${student.first_name} ${student.last_name} has been successfully assigned to ${selectedElective.name}`
    });
  };

  const handleStudentsAssigned = (studentIds) => {
    // Refresh electives to update enrollment count
    fetchElectives();
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Students Assigned',
      message: `${studentIds.length} student${studentIds.length > 1 ? 's' : ''} have been successfully assigned to ${selectedElective.name}`
    });
  };

  const handleStudentRemoved = (studentId) => {
    // Refresh electives to update enrollment count
    fetchElectives();
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Student Removed',
      message: 'Student has been successfully removed from the elective'
    });
  };

  const handleDeleteElective = (elective) => {
    setSelectedElective(elective);
    setShowDeleteModal(true);
  };

  const handleAssignStudent = (elective) => {
    setSelectedElective(elective);
    setShowAssignModal(true);
  };

  const handleBulkAssignStudents = (elective) => {
    setSelectedElective(elective);
    setShowBulkAssignModal(true);
  };

  const handleRemoveStudents = (elective) => {
    setSelectedElective(elective);
    setShowRemoveModal(true);
  };

  const handleViewStudents = (elective) => {
    navigate(`/admin/electives/${elective.id}/students`);
  };

  const handleManageContent = (elective) => {
    // Navigate to elective detail page with content management
    navigate(`/electives/${elective.id}`);
  };

  const handleCreateAttendanceSession = (elective) => {
    setSelectedElective(elective);
    setShowCreateAttendanceModal(true);
  };

  const handleAttendanceSessionCreated = (session) => {
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Success',
      message: 'Attendance session created successfully!'
    });
    // Refresh electives to update session count
    fetchElectives();
  };

  const confirmDeleteElective = async () => {
    if (!selectedElective) return;
    
    setDeleteLoading(true);
    
    try {
      // Call the API to delete elective
      await apiService.deleteElective(selectedElective.id);
      
      // Remove elective from the list
      setElectives(prev => prev.filter(elective => elective.id !== selectedElective.id));
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setSelectedElective(null);
      
      // Show success message
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Elective Deleted',
        message: `Elective ${selectedElective.code} - ${selectedElective.name} has been deleted successfully.`
      });
      
    } catch (error) {
      console.error('Error deleting elective:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete elective. Please try again.'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <ElectiveManagementContainer>
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <FiBookOpen size={32} />
          <p>Loading electives...</p>
        </div>
      </ElectiveManagementContainer>
    );
  }

  return (
    <ElectiveManagementContainer>
      <Header>
        <Title>Elective Management</Title>
        {user?.role === 'admin' && (
          <AddButton onClick={handleAddElective}>
            <FiPlus size={16} />
            Add Elective
          </AddButton>
        )}
      </Header>

      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Search electives..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterButton>
          <FiFilter size={16} />
          Filter
        </FilterButton>
      </SearchBar>

      <Filters>
        <FilterButton 
          className={activeFilter === 'all' ? 'active' : ''}
          onClick={() => setActiveFilter('all')}
        >
          All Electives
        </FilterButton>
        <FilterButton 
          className={activeFilter === 'active' ? 'active' : ''}
          onClick={() => setActiveFilter('active')}
        >
          Active
        </FilterButton>
        <FilterButton 
          className={activeFilter === 'inactive' ? 'active' : ''}
          onClick={() => setActiveFilter('inactive')}
        >
          Inactive
        </FilterButton>
      </Filters>

      {filteredElectives.length > 0 ? (
        <ElectivesGrid>
          {filteredElectives.map((elective, index) => (
            <ElectiveCard
              key={elective.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <ElectiveHeader>
                <ElectiveInfo>
                  <ElectiveCode>{elective.code}</ElectiveCode>
                  <ElectiveName>{elective.name}</ElectiveName>
                </ElectiveInfo>
                <ActionButtons>
                  {/* First Row: Student Management Buttons */}
                  <ButtonRow>
                    <Tooltip content="View Students">
                      <PrimaryActionButton onClick={() => handleViewStudents(elective)}>
                        <FiUsers size={16} />
                      </PrimaryActionButton>
                    </Tooltip>
                    {(user?.role === 'admin' || user?.role === 'instructor') && (
                      <>
                        <Tooltip content="Assign Single Student">
                          <PrimaryActionButton onClick={() => handleAssignStudent(elective)}>
                            <FiUserPlus size={16} />
                          </PrimaryActionButton>
                        </Tooltip>
                        <Tooltip content="Bulk Assign Students">
                          <PrimaryActionButton onClick={() => handleBulkAssignStudents(elective)}>
                            <FiBulkUsers size={16} />
                          </PrimaryActionButton>
                        </Tooltip>
                        <Tooltip content="Remove Students">
                          <PrimaryActionButton onClick={() => handleRemoveStudents(elective)}>
                            <FiUserMinus size={16} />
                          </PrimaryActionButton>
                        </Tooltip>
                      </>
                    )}
                  </ButtonRow>
                  
                  {/* Second Row: Content & Elective Management Buttons */}
                  <ButtonRow>
                    {(user?.role === 'admin' || user?.role === 'instructor') && (
                      <>
                        <Tooltip content="Manage Content">
                          <PrimaryActionButton onClick={() => handleManageContent(elective)}>
                            <FiFileText size={16} />
                          </PrimaryActionButton>
                        </Tooltip>
                        <Tooltip content="Create Attendance Session">
                          <PrimaryActionButton onClick={() => handleCreateAttendanceSession(elective)}>
                            <FiCalendar size={16} />
                          </PrimaryActionButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip content="Edit Elective">
                      <PrimaryActionButton onClick={() => handleEditElective(elective)}>
                        <FiEdit size={16} />
                      </PrimaryActionButton>
                    </Tooltip>
                    {user?.role === 'admin' && (
                      <Tooltip content="Delete Elective">
                        <PrimaryActionButton 
                          onClick={() => handleDeleteElective(elective)}
                        >
                          <FiTrash2 size={16} />
                        </PrimaryActionButton>
                      </Tooltip>
                    )}
                  </ButtonRow>
                </ActionButtons>
              </ElectiveHeader>

              <ElectiveDescription>{elective.description}</ElectiveDescription>

              <ElectiveInstructor>
                <FiUsers size={14} />
                Instructor{elective.instructors && elective.instructors.length > 1 ? 's' : ''}: {
                  elective.instructors && Array.isArray(elective.instructors) && elective.instructors.length > 0
                    ? elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(', ')
                    : `${elective.instructor_first_name || ''} ${elective.instructor_last_name || ''}`.trim() || 'N/A'
                }
              </ElectiveInstructor>

              <ElectiveStats>
                <Stat>
                  <FiUsers size={14} />
                  {elective.enrolled_students || 0}/{elective.max_students} students
                </Stat>
              </ElectiveStats>

              <ElectiveDates>
                <div>
                  <div>Start: {formatDateForDisplay(elective.start_date, 'MMM d, yyyy')}</div>
                  <div>End: {formatDateForDisplay(elective.end_date, 'MMM d, yyyy')}</div>
                </div>
                <StatusBadge className={getElectiveStatus(elective)}>
                  {getElectiveStatus(elective)}
                </StatusBadge>
              </ElectiveDates>
            </ElectiveCard>
          ))}
        </ElectivesGrid>
      ) : (
        <EmptyState>
          <FiBookOpen />
          <h3>No electives found</h3>
          <p>
            {searchTerm 
              ? `No electives match "${searchTerm}"`
              : 'There are no electives in the system yet.'
            }
          </p>
        </EmptyState>
      )}

      <AddElectiveModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onElectiveAdded={handleElectiveAdded}
      />

      <EditElectiveModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedElective(null);
        }}
        elective={selectedElective}
        onElectiveUpdated={handleElectiveUpdated}
      />

      <AssignElectiveStudentModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedElective(null);
        }}
        elective={selectedElective}
        onStudentAssigned={handleStudentAssigned}
      />

      <BulkAssignElectiveModal
        isOpen={showBulkAssignModal}
        onClose={() => {
          setShowBulkAssignModal(false);
          setSelectedElective(null);
        }}
        elective={selectedElective}
        onStudentsAssigned={handleStudentsAssigned}
      />

      <RemoveElectiveStudentModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false);
          setSelectedElective(null);
        }}
        elective={selectedElective}
        onStudentRemoved={handleStudentRemoved}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedElective(null);
        }}
        onConfirm={confirmDeleteElective}
        item={selectedElective}
        itemType="elective"
        loading={deleteLoading}
      />

      <CreateAttendanceModal
        isOpen={showCreateAttendanceModal}
        onClose={() => {
          setShowCreateAttendanceModal(false);
          setSelectedElective(null);
        }}
        elective={selectedElective}
        onSessionCreated={handleAttendanceSessionCreated}
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </ElectiveManagementContainer>
  );
}

export default ElectiveManagement;

